/**
 * Refuse to send Authorization headers to any origin not in the allowlist.
 *
 * Defense in depth for explicit URLs: backend selection is owned by the
 * dev server or deployment configuration, but individual callers can still
 * supply a URL. Only trusted origins may receive an Authorization header.
 *
 * The check applies to both the global axios instance (used by ~40 hooks
 * that call axios.get/post directly with Authorization in per-call headers)
 * AND every instance returned by createAuthenticatedClient (which sets
 * Authorization as a default header). Each instance keeps its own
 * interceptor chain, so the guard has to be installed on both.
 */
import * as Sentry from "@sentry/react";
import type {
  AxiosHeaders,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import {
  DEV_ALLOWED_HOST_SUFFIXES,
  DEV_ALLOWED_LITERAL_HOSTS,
} from "@/config/devAllowedHosts";

// Resolve the trusted backend origin once at module load. VITE_DALE_API_URL is
// inlined by Vite at build time and is set per environment in Doppler:
//   prd build  -> https://api.sameday.ai
//   stg build  -> https://api-stg.sameday.ai
//   dev build  -> https://api-stg.sameday.ai
// We compare full `.origin` (scheme + host + port), not just hostname — so a
// tampered `http://api.sameday.ai` (cleartext) or non-default port doesn't
// pass as a substitute for the configured https origin.
const CONFIGURED_API_ORIGIN: string | null = (() => {
  try {
    return new URL(import.meta.env.VITE_DALE_API_URL).origin;
  } catch {
    return null;
  }
})();

const isDevAllowedHost = (hostname: string): boolean =>
  DEV_ALLOWED_LITERAL_HOSTS.includes(hostname) ||
  DEV_ALLOWED_HOST_SUFFIXES.some((suffix) => hostname.endsWith(suffix));

/**
 * Resolve the request URL the way axios will resolve it. Handles all three
 * cases without throwing on relative baseURLs (e.g. `/api` from the dev proxy):
 *   - absolute `url`                          -> uses url directly
 *   - absolute `baseURL` + relative `url`     -> uses baseURL's origin
 *   - relative `baseURL` + relative `url`     -> resolves against window.location.origin
 */
const resolveRequestUrl = (
  url: string | undefined,
  baseURL: string | undefined,
): URL | null => {
  try {
    const absoluteBase = baseURL
      ? new URL(baseURL, window.location.origin).toString()
      : window.location.origin;
    return new URL(url ?? "", absoluteBase);
  } catch {
    return null;
  }
};

const isFirstPartyResolvedOrigin = (resolved: URL): boolean => {
  // Same-origin (e.g. /api via Vite dev proxy, or any prod backend served
  // from the same domain) is always safe — there's no third party involved.
  if (resolved.origin === window.location.origin) return true;

  if (resolved.origin === CONFIGURED_API_ORIGIN) return true;

  if (import.meta.env.DEV && isDevAllowedHost(resolved.hostname)) return true;

  return false;
};

export const isAllowedAuthOrigin = (
  url: string | undefined,
  baseURL: string | undefined,
): boolean => {
  const resolved = resolveRequestUrl(url, baseURL);
  if (!resolved) return false;
  return isFirstPartyResolvedOrigin(resolved);
};

/**
 * Which third party is this request bound for, if any? `null` means "ours" —
 * the caller should treat the failure as its own API's.
 *
 * Deliberately not the inverse of `isAllowedAuthOrigin`: the two disagree on
 * the unresolvable case because their callers need opposite defaults.
 * - Auth fails **closed** — an origin we cannot read never receives a Bearer
 *   token. A false negative costs a request; a false positive leaks a JWT.
 * - Reporting fails **open** — an origin we cannot read is attributed to us and
 *   still reported. A URL axios could not even parse is a client-side bug we
 *   want in Sentry, and staying silent would delete that whole class of failure.
 *
 * Used by `reportApiError` to tell a third party's outage apart from our own.
 * Third parties resolve to their hostname, which the caller sanitizes before
 * it reaches Sentry because the value may have come from user-influenced data.
 */
export const resolveThirdPartyVendor = (
  url: string | undefined,
  baseURL: string | undefined,
): string | null => {
  const resolved = resolveRequestUrl(url, baseURL);
  if (!resolved) return null;
  if (isFirstPartyResolvedOrigin(resolved)) return null;
  return resolved.hostname;
};

const hasAuthHeader = (config: InternalAxiosRequestConfig): boolean => {
  const headers: AxiosHeaders = config.headers;
  return headers.has("Authorization");
};

const safeOrigin = (
  url: string | undefined,
  baseURL: string | undefined,
): string => resolveRequestUrl(url, baseURL)?.origin ?? "<invalid url>";

export const attachAuthOriginGuard = (instance: AxiosInstance): void => {
  instance.interceptors.request.use((config) => {
    if (!hasAuthHeader(config)) return config;
    if (isAllowedAuthOrigin(config.url, config.baseURL)) return config;

    const origin = safeOrigin(config.url, config.baseURL);
    const message = `Refusing to send Authorization header to ${origin}`;
    Sentry.captureMessage(message, "error");
    throw new Error(message);
  });
};
